import { INSTAGRAM_URL, INSTAGRAM_HANDLE, CONTACT_EMAIL, SERVICE_AREA, FULFILLMENT } from '../config/site';

/* ── Social icon ──────────────────────────────────────────── */

function InstagramIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="18" cy="6" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ── Footer link column ───────────────────────────────────── */

interface FooterColumnProps {
  title: string;
  links: { label: string; href: string; external?: boolean }[];
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div>
      <h4 className="font-inter text-xs font-medium tracking-[0.25em] uppercase text-white/90 mb-6">
        {title}
      </h4>
      <ul className="list-none p-0 m-0 space-y-2">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="font-inter text-xs text-white/60 no-underline hover:text-white/90 transition-colors"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Footer ───────────────────────────────────────────────── */

export default function Footer() {
  return (
    <footer className="bg-sage-dark text-white">
      {/* Link columns */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
          <FooterColumn
            title="Explore"
            links={[
              { label: 'How it works', href: '#how-it-works' },
              { label: 'Flavors', href: '#flavors' },
              { label: 'Gallery', href: '#gallery' },
              { label: 'Reviews', href: '#reviews' },
            ]}
          />
          <FooterColumn
            title="Order"
            links={[
              { label: 'Order on Instagram', href: INSTAGRAM_URL, external: true },
              { label: 'Send a request', href: '#order' },
              { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}`, external: true },
            ]}
          />
          <div className="text-center sm:text-left">
            <h4 className="font-inter text-xs font-medium tracking-[0.25em] uppercase text-white/90 mb-6">
              Pickup &amp; Delivery
            </h4>
            <p className="font-inter text-xs text-white/60 leading-relaxed max-w-[16rem] mx-auto sm:mx-0">
              {SERVICE_AREA}
              <br />
              {FULFILLMENT}
            </p>
          </div>
        </div>
      </div>

      {/* Logo + social */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <img
            src="/images/cremeberry-logo.jpg"
            alt="Crème Berry"
            className="w-20 h-20 rounded-full object-cover mx-auto mb-6"
          />
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Instagram ${INSTAGRAM_HANDLE}`}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors no-underline"
          >
            <InstagramIcon />
            <span className="font-inter text-xs tracking-[0.15em]">{INSTAGRAM_HANDLE}</span>
          </a>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2">
          <span className="font-inter text-[10px] text-white/40 tracking-[0.05em]">
            © {new Date().getFullYear()} Crème Berry. Made with love in San Francisco.
          </span>
        </div>
      </div>
    </footer>
  );
}
